import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import {
  Box,
  Container,
  Stack,
  Typography,
  Button,
  TextField,
  MenuItem,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';

import Loader from 'src/common/Loader';
import GroupList from 'src/components/groups/GroupList';
import NewGroup from 'src/components/groups/NewGroup';

import { getGroupList, setGroup } from 'src/redux/groupSlice';
import { GROUP_TYPES } from 'src/utils/constants';
import usePersistentListFilters from 'src/hooks/usePersistentListFilters';
import ListRefreshButton from 'src/common/ListRefreshButton';
import ColumnSearch from 'src/common/ColumnSearch';

const Groups = () => {
  const dispatch = useDispatch();
  const { search, setSearch } = usePersistentListFilters();

  const { groupList, groupType, loading } = useSelector(
    (state) => state.groups
  );
  const isDepartmentType = groupType === 'department';
  const isContractorType = groupType === 'contractor';
  let entityLabel = 'Group';
  if (isDepartmentType) entityLabel = 'Department';
  if (isContractorType) entityLabel = 'Contractor';

  const [openModal, setOpenModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [searchText, setSearchText] = useState(search);
  const [searchFilters, setSearchFilters] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    dispatch(getGroupList({ groupType, params: { ...searchFilters, name: searchFilters.name || searchText } }));
  }, [dispatch, groupType, searchText, searchFilters, refreshKey]);

  const handleEditGroup = (group) => {
    setSelectedGroup(group);
    setOpenModal(true);
  };

  return (
    <>
      <Helmet>
        <title>{`${entityLabel} | Retrack FMS`}</title>
      </Helmet>
      <Box
        className="transaction-style-page transaction-style-duplicate-header"
        sx={{
          backgroundColor: '#FFFFFF',
          minHeight: 'calc(100vh - 64px)',
          height: { xs: 'auto', md: 'calc(100vh - 64px)' },
          overflow: { xs: 'auto', md: 'hidden' },
          py: 2
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            spacing={3}
            sx={{ py: 2, mb: 2, flexShrink: 0 }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: '#111827',
                  letterSpacing: '-0.02em',
                  mb: 0.5
                }}
              >
                {entityLabel.toUpperCase()}
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                {`Manage and organize ${entityLabel.toLowerCase()} records`}
              </Typography>
            </Box>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', sm: 'flex-start' }}
              useFlexGap
              flexWrap={{ xs: 'nowrap', sm: 'wrap' }}
              sx={{ width: { xs: '100%', md: 'auto' } }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="flex-start"
                sx={{ width: { xs: '100%', md: 'max-content' }, flexShrink: 0 }}
              >
                <ColumnSearch
                  fields={[
                    { key: 'name', label: `${entityLabel} Name`, defaultChecked: true },
                    { key: 'id', label: 'ID' }
                  ]}
                  values={searchFilters}
                  onApply={(values) => { setSearchFilters(values); setSearchText(''); setSearch(''); }}
                  onClear={() => { setSearchFilters({}); setSearchText(''); setSearch(''); }}
                  containerSx={{
                    width: { xs: '100%', md: 260 },
                    flexShrink: 0
                  }}
                  sx={{ width: { xs: '100%', md: 260 } }}
                />
                <ListRefreshButton onRefresh={() => setRefreshKey((value) => value + 1)} />
              </Stack>

              <TextField
                select
                size="small"
                value={groupType}
                onChange={(e) => dispatch(setGroup(e.target.value))}
                sx={{
                  minWidth: 200,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: '#F9FAFB'
                  }
                }}
              >
                {GROUP_TYPES.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </TextField>

              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setSelectedGroup(null);
                  setOpenModal(true);
                }}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  minHeight: 40,
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
              >
                {`Add ${entityLabel}`}
              </Button>
            </Stack>
          </Stack>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden'
            }}
          >
            {loading ? (
              <Loader />
            ) : (
              <GroupList
                groups={groupList}
                onEditGroup={handleEditGroup}
              />
            )}
          </Box>
        </Container>

        <NewGroup
          open={openModal}
          initialValues={selectedGroup}
          handleClose={() => {
            setOpenModal(false);
            setSelectedGroup(null);
          }}
        />
      </Box>
    </>
  );
};

export default Groups;
